import { Controller } from "@hotwired/stimulus"
import { Modal, Tab } from "bootstrap"
import { initRemoteAssociationSelects } from "../helpers/remote_association_select"

export default class extends Controller {
  static targets = ["modal", "title", "loader", "form", "content", "notice"]
  static values = { userId: Number, error: String }

  connect() {
    this.connected = true
    this.modalShown = () => {
      this.modalVisible = true
      if (this.closeAfterShow) {
        this.closeAfterShow = false
        Modal.getInstance(this.modalTarget)?.hide()
      }
    }
    this.modalHidden = () => {
      this.modalVisible = false
      this.closeAfterShow = false
      this.openRequest?.abort()
      this.clearForm()
    }
    this.modalHiding = event => { if (this.saving) event.preventDefault() }
    this.modalTarget.addEventListener("hidden.bs.modal", this.modalHidden)
    this.modalTarget.addEventListener("shown.bs.modal", this.modalShown)
    this.modalTarget.addEventListener("hide.bs.modal", this.modalHiding)
    if (["#abilities", "#created-enrollments"].includes(window.location.hash)) {
      const button = document.querySelector(`button[data-bs-target="${window.location.hash}"]`)
      if (button) Tab.getOrCreateInstance(button).show()
    }
  }

  disconnect() {
    this.connected = false
    this.openRequest?.abort()
    this.modalTarget.removeEventListener("hidden.bs.modal", this.modalHidden)
    this.modalTarget.removeEventListener("shown.bs.modal", this.modalShown)
    this.modalTarget.removeEventListener("hide.bs.modal", this.modalHiding)
    this.clearForm()
    const modal = Modal.getInstance(this.modalTarget)
    if (modal && this.modalTarget.classList.contains("show")) {
      this.modalTarget.addEventListener("hidden.bs.modal", () => modal.dispose(), { once: true })
      if (this.modalVisible) {
        modal.hide()
      } else {
        this.modalTarget.addEventListener("shown.bs.modal", () => modal.hide(), { once: true })
      }
    } else {
      modal?.dispose()
    }
  }

  async open(event) {
    event.preventDefault()
    if (this.saving) return
    this.openRequest?.abort()
    const request = new AbortController()
    this.openRequest = request
    this.noticeTarget.classList.add("d-none")
    this.titleTarget.textContent = event.currentTarget.dataset.userAbilitiesTitle
    this.clearForm()
    this.loaderTarget.classList.remove("d-none")
    Modal.getOrCreateInstance(this.modalTarget).show(event.currentTarget)

    try {
      const response = await fetch(event.currentTarget.dataset.userAbilitiesUrl, {
        headers: { Accept: "text/html" }, credentials: "same-origin", signal: request.signal
      })
      if (!response.ok || response.redirected) throw new Error()
      const html = await response.text()
      if (!this.connected || this.openRequest !== request || request.signal.aborted) return
      this.renderForm(html)
    } catch (error) {
      if (error.name !== "AbortError" && !request.signal.aborted) this.showModalError()
    } finally {
      if (this.connected && this.openRequest === request) this.loaderTarget.classList.add("d-none")
    }
  }

  async save(event) {
    event.preventDefault()
    if (this.saving) return
    this.saving = true
    const form = event.target.closest("form")
    form.querySelectorAll('[type="submit"]').forEach(button => { button.disabled = true })
    try {
      const response = await fetch(form.action, {
        method: form.method.toUpperCase(), body: new FormData(form), headers: this.headers(), credentials: "same-origin"
      })
      if (!this.connected) return
      if (response.redirected) throw new Error()
      if (response.status === 422) {
        this.renderForm(await response.text())
        return
      }
      if (!response.ok) throw new Error()
      this.updateContent(await response.json())
      this.saving = false
      if (this.modalVisible) {
        Modal.getOrCreateInstance(this.modalTarget).hide()
      } else {
        this.closeAfterShow = true
      }
    } catch (_error) {
      if (this.connected) this.showModalError()
    } finally {
      this.saving = false
      form.querySelectorAll('[type="submit"]').forEach(button => { button.disabled = false })
    }
  }

  async destroy(event) {
    const link = event.target.closest('a[data-method="delete"]')
    if (!link) return
    event.preventDefault()
    event.stopPropagation()
    if (this.deleting || !window.confirm(link.dataset.confirm)) return
    this.deleting = true
    try {
      const response = await fetch(link.href, { method: "DELETE", headers: this.headers(), credentials: "same-origin" })
      if (!this.connected) return
      if (!response.ok || response.redirected) throw new Error()
      this.updateContent(await response.json())
    } catch (_error) {
      if (this.connected) this.showNotice(this.errorValue, true)
    } finally {
      this.deleting = false
    }
  }

  renderForm(html) {
    this.clearForm()
    this.formTarget.innerHTML = html
    initRemoteAssociationSelects(this.formTarget)
  }

  showModalError() {
    this.formTarget.querySelector("[data-ability-error]")?.remove()
    const error = document.createElement("div")
    error.className = "alert alert-danger"
    error.dataset.abilityError = "true"
    error.setAttribute("role", "alert")
    error.textContent = this.errorValue
    this.formTarget.prepend(error)
  }

  clearForm() {
    this.formTarget.querySelectorAll("select").forEach(select => select.tomselect?.destroy())
    this.formTarget.innerHTML = ""
  }

  updateContent(data) {
    this.contentTarget.innerHTML = data.html
    document.querySelectorAll(`[data-user-abilities-count="${this.userIdValue}"]`).forEach(badge => {
      badge.textContent = data.count
      badge.classList.toggle("d-none", data.count === 0)
    })
    this.showNotice(data.notice)
  }

  showNotice(message, error = false) {
    this.noticeTarget.textContent = message
    this.noticeTarget.classList.remove("d-none")
    this.noticeTarget.classList.toggle("alert-danger", error)
    this.noticeTarget.classList.toggle("alert-success", !error)
  }

  headers() {
    return { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content }
  }
}
